<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { ElMenu, ElMenuItem, ElSubMenu } from "element-plus";
import searchIcon from "@/assets/search.svg";
import logo from "@/assets/logo.png";
import tabConfig from "@/config/tabConfig";

const route = useRoute();
const router = useRouter();

// 从 tabConfig 构建菜单项
const menuItems = ref([
  {
    name: "关于研究院",
    path: "/about",
    key: "about",
    children: tabConfig.about.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
  {
    name: "科学研究",
    path: "/research",
    key: "research",
    children: tabConfig.research.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },

  {
    name: "人才培养",
    path: "/education",
    key: "education",
    children: tabConfig.education.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
  {
    name: "创新基地",
    path: "/innovation",
    key: "innovation",
    children: tabConfig.innovation.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
  {
    name: "党群文化",
    path: "/party",
    key: "party",
    children: tabConfig.party.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
  {
    name: "人力资源",
    path: "/hr",
    key: "hr",
    children: tabConfig.hr.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
  {
    name: "数字平台",
    path: "/digital",
    key: "digital",
    children: tabConfig.digital.mainTabs.map((tab) => ({
      name: tab.name,
      key: tab.value,
    })),
  },
]);

const isMenuOpen = ref(false);
const isSearchActive = ref(false);
const searchValue = ref("");
const activeMainMenu = ref("");
const activeSubMenu = ref("");
const searchModalVisible = ref(false);
const searchInputRef = ref(null);
const mobileSearchInputRef = ref(null);

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const toggleSearch = () => {
  isSearchActive.value = !isSearchActive.value;
  if (isSearchActive.value) {
    nextTick(() => {
      searchInputRef.value?.focus();
    });
  }
};

const toggleSearchModal = () => {
  if (window.innerWidth <= 1024) {
    searchModalVisible.value = !searchModalVisible.value;
    if (!searchModalVisible.value) {
      searchValue.value = "";
    } else {
      nextTick(() => {
        mobileSearchInputRef.value?.focus();
      });
    }
  } else {
    toggleSearch();
  }
};

const handleSearch = () => {
  if (searchValue.value.trim()) {
    router.push({
      path: "/search",
      query: {
        keyword: searchValue.value.trim(),
      },
    });
    searchValue.value = "";
    isSearchActive.value = false;
  }
};

const handleKeyPress = (e) => {
  if (e.key === "Enter") {
    handleSearch();
  }
};

const handleMenuClick = (item, subItem) => {
  // 数字平台特殊处理 - 直接跳转到外部链接
  if (item.key === "digital") {
    if (subItem) {
      // 查找对应的配置项获取 URL
      const digitalTab = tabConfig.digital.mainTabs.find(
        (tab) => tab.value === subItem.key
      );
      if (digitalTab && digitalTab.url) {
        // 打开新窗口跳转到配置的 URL
        window.open(digitalTab.url, "_blank");
        return;
      }
    } else {
      // 如果只点击了数字平台主菜单，默认打开第一个子菜单的 URL
      const firstTab = tabConfig.digital.mainTabs[0];
      if (firstTab && firstTab.url) {
        window.open(firstTab.url, "_blank");
        return;
      }
    }
  }

  const query = { subTab: subItem?.key || "" };
  const section = tabConfig[item.key];

  if (!subItem) {
    // 如果没有选择子菜单，使用第一个子标签
    if (section?.subTabs?.overview) {
      // 如果有 overview 子标签配置（比如党群文化）
      const defaultItem = section.subTabs.overview[0];
      query.subTab = "overview";
      query.subSubTab = defaultItem.title;
      query.categoryId = defaultItem.categoryId;
    } else if (section?.mainTabs?.[0]) {
      // 否则使用第一个主标签
      const defaultTab = section.mainTabs[0];
      query.subTab = defaultTab.value;
      // 检查是否有叶子节点
      const subTabs = section.subTabs?.[defaultTab.value];
      if (subTabs && subTabs.length > 0) {
        query.categoryId = subTabs[0].categoryId;
        query.subSubTab = subTabs[0].title;
      } else {
        query.categoryId = defaultTab.categoryId;
      }
    }
  } else {
    // 选择了特定子菜单
    if (section?.mainTabs) {
      const mainTab = section.mainTabs.find((tab) => tab.value === subItem.key);
      if (mainTab) {
        // 检查是否有叶子节点
        const subTabs = section.subTabs?.[mainTab.value];
        if (subTabs && subTabs.length > 0) {
          // 如果有叶子节点，使用第一个叶子节点的分类ID
          query.categoryId = subTabs[0].categoryId;
          query.subSubTab = subTabs[0].title;
        } else {
          // 如果没有叶子节点，使用当前节点的分类ID
          query.categoryId = mainTab.categoryId;
        }

        // 如果是研究领域，需要清除可能存在的 showDetail 和 articleId
        if (item.key === "research") {
          query.showDetail = undefined;
          query.articleId = undefined;
        }
      }
    }
  }

  router.push({
    path: item.path,
    query,
  });
};

const initActiveState = () => {
  // 从路径中获取当前主菜单
  const mainPath = route.path.slice(1); // 去掉开头的 '/'
  activeMainMenu.value = mainPath;

  // 从查询参数获取子菜单
  const { subTab } = route.query;
  activeSubMenu.value = subTab || "";
};

watch(route, () => {
  initActiveState();
});

onMounted(() => {
  initActiveState();
});

const isPartyPage = computed(() => route.path.startsWith("/party"));

const handleMobileSearch = () => {
  if (searchValue.value.trim()) {
    router.push({
      path: "/search",
      query: {
        keyword: searchValue.value.trim(),
      },
    });
    searchValue.value = "";
    searchModalVisible.value = false;
  }
};
</script>

<template>
  <header class="header" :class="{ 'party-theme': isPartyPage }">
    <div class="header-bg"></div>
    <div class="header-content">
      <div class="header-content-left">
        <div class="logo">
          <RouterLink to="/">
            <img :src="logo" alt="上海交通大学" />
          </RouterLink>
        </div>

        <!-- 移动端汉堡菜单按钮 -->
        <div
          class="hamburger"
          :class="{ 'is-active': isMenuOpen }"
          @click="toggleMenu"
        >
          <span></span>
          <span></span>
          <span></span>
        </div>

        <!-- 桌面端导航菜单 -->
        <nav class="desktop-nav" :class="{ 'party-theme': isPartyPage }">
          <div class="nav-menu">
            <div
              v-for="item in menuItems"
              :key="item.key"
              class="nav-item-wrapper"
            >
              <div
                class="nav-item"
                :class="{ active: activeMainMenu === item.key }"
                @click="handleMenuClick(item, null)"
              >
                <span>{{ item.name }}</span>
              </div>
              <div class="submenu" v-if="item.children && item.children.length">
                <div
                  v-for="child in item.children"
                  :key="child.key"
                  class="submenu-item"
                  :class="{ active: activeSubMenu === child.key }"
                  @click="handleMenuClick(item, child)"
                >
                  {{ child.name }}
                </div>
              </div>
            </div>
          </div>
        </nav>

        <!-- 移动端导航菜单 -->
        <div class="mobile-nav-wrapper" v-if="isMenuOpen">
          <div class="mobile-nav-overlay" @click="isMenuOpen = false"></div>
          <div class="mobile-nav">
            <!-- 添加搜索框到导航顶部 -->
            <div class="mobile-search">
              <div class="search-input-wrapper">
                <input
                  ref="mobileSearchInputRef"
                  type="text"
                  v-model="searchValue"
                  placeholder="输入内容以搜索"
                  @keypress.enter="handleMobileSearch"
                />
                <button @click="handleMobileSearch">
                  <img :src="searchIcon" alt="搜索" />
                </button>
              </div>
            </div>
            <el-menu
              :default-active="activeSubMenu"
              class="mobile-menu"
              background-color="#0264C4"
              text-color="#fff"
              active-text-color="#fff"
              :unique-opened="true"
            >
              <el-sub-menu
                v-for="item in menuItems"
                :key="item.key"
                :index="item.key"
              >
                <template #title>
                  <span>{{ item.name }}</span>
                </template>
                <el-menu-item
                  v-for="child in item.children"
                  :key="child.key"
                  :index="child.key"
                  @click="handleMenuClick(item, child)"
                >
                  {{ child.name }}
                </el-menu-item>
              </el-sub-menu>
            </el-menu>
          </div>
        </div>
      </div>
      <div class="header-content-right">
        <div class="search" :class="{ 'search-active': isSearchActive }">
          <input
            ref="searchInputRef"
            type="text"
            placeholder="输入内容以搜索"
            v-model="searchValue"
            @keypress="handleKeyPress"
            @blur="isSearchActive = false"
          />
          <button type="button" @click="toggleSearch">
            <img :src="searchIcon" alt="搜索" />
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
.header {
  position: relative;
  width: 100%;
  height: 100px;
  background: #0264c4;
  color: white;
  position: sticky;
  top: 0;
  z-index: 9999999;
  display: flex;
  justify-content: center;
  padding: 0 clamp(20px, 5vw, 100px);
  box-sizing: border-box;
  --header-height: 100px;
}

.header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/header-bg.png");
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  pointer-events: none;
}

.header-content {
  position: relative;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-sizing: border-box;
}

.header-content-right {
  display: flex;
  align-items: center;
  width: 280px;
  justify-content: flex-end;
  margin-left: 20px;
  flex-shrink: 0;
}

.header-content-left {
  display: flex;
  align-items: center;
  gap: clamp(10px, 1vw, 20px);
  flex: 1;
  margin-right: auto;
}

.logo {
  display: flex;
  align-items: center;
  margin-left: clamp(10px, 1.5vw, 30px);
}

.logo a {
  display: flex;
  align-items: center;
  text-decoration: none;
  color: white;
}

.logo img {
  height: 50px;
  transition: height 0.3s ease;
}

.desktop-nav {
  display: flex;
  height: 100%;
  align-items: center;
}

.nav-menu {
  display: flex;
  height: 100%;
  align-items: center;
}

.nav-item-wrapper {
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
  margin: 0 10px;
}

.nav-item {
  color: white;
  padding: 20px 24px;
  height: 100%;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-size: 20px;
  transition: all 0.3s ease;
  position: relative;
  white-space: nowrap;
}

.nav-item::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: white;
  transform: scaleX(0);
  transition: transform 0.3s ease;
  transform-origin: center;
}

.nav-item:hover {
  transform: scale(1.05);
}

.nav-item:hover::after {
  transform: scaleX(0.8);
}

.nav-item.active {
  font-weight: 500;
  transform: scale(1.05);
}

.nav-item.active::after {
  transform: scaleX(1);
}

.search {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
}

.search input {
  position: absolute;
  right: 48px;
  width: 0;
  height: 48px;
  background: rgb(255, 255, 255);
  border: none;
  color: #333;
  padding: 0;
  outline: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
  border-radius: 4px;
}

.search.search-active input {
  width: 200px;
  padding: 0 16px;
  opacity: 1;
}

.search button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  min-height: 48px;
  position: relative;
  z-index: 2;
}

.search button img {
  width: 32px;
  height: 32px;
  filter: brightness(0) invert(1);
}

/* 汉堡菜单样式 */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  cursor: pointer;
  margin-left: auto;
  position: relative;
  z-index: 101;
  padding: 0;
}

.hamburger span {
  display: block;
  height: 2px;
  width: 100%;
  background-color: white;
  transition: all 0.3s ease;
  position: absolute;
  left: 0;
}

.hamburger span:nth-child(1) {
  top: 0;
}

.hamburger span:nth-child(2) {
  top: 8px;
}

.hamburger span:nth-child(3) {
  top: 16px;
}

.hamburger.is-active span:nth-child(1) {
  transform: rotate(45deg);
  top: 8px;
}

.hamburger.is-active span:nth-child(2) {
  opacity: 0;
  transform: translateX(-20px);
}

.hamburger.is-active span:nth-child(3) {
  transform: rotate(-45deg);
  top: 8px;
}

/* 重新设计媒体查询 */
@media (max-width: 1024px) {
  .header {
    padding: 0 20px;
    height: 80px;
    --header-height: 80px;
  }

  .header-content-left {
    max-width: 100%;
    position: static;
  }

  .header-content-right {
    display: none;
  }

  .logo {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  }

  .hamburger {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
  }

  .desktop-nav {
    display: none;
  }

  .mobile-nav-wrapper {
    display: block;
    position: fixed;
    top: 80px;
    left: 0;
    width: 100%;
    height: calc(100vh - 80px);
    z-index: 99;
    overflow-y: auto;
  }

  .logo img {
    height: 40px;
  }
}

@media (max-width: 768px) {
  .header {
    padding: 0 15px;
    height: 70px;
    --header-height: 70px;
  }

  .mobile-nav-wrapper {
    top: 70px;
    height: calc(100vh - 70px);
  }

  .logo {
    left: 15px;
  }

  :deep(.el-sub-menu__title) {
    height: 50px !important;
    line-height: 50px !important;
    font-size: 15px;
  }

  :deep(.el-menu-item) {
    height: 44px !important;
    line-height: 44px !important;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .header {
    padding: 0 12px;
    height: 60px;
    --header-height: 60px;
  }

  .mobile-nav-wrapper {
    top: 60px;
    height: calc(100vh - 60px);
  }

  .logo {
    left: 12px;
  }
}

/* 桌面端子菜单样式 */
.submenu {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  min-width: 180px;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 8px 0;
}

.nav-item-wrapper:hover .submenu {
  opacity: 1;
  visibility: visible;
  transform: translateX(-50%) translateY(0);
}

.submenu-item {
  padding: 16px 28px;
  color: #333;
  white-space: nowrap;
  transition: all 0.3s ease;
  font-size: 16px;
  display: block;
  text-align: center;
}

.submenu-item:hover {
  background: rgba(2, 100, 196, 0.1);
  color: #0264c4;
}

.submenu-item.active {
  background: rgba(2, 100, 196, 0.1);
  color: #0264c4;
  font-weight: 500;
}

/* 移动端导航搜索框样式 */
.mobile-search {
  padding: 16px;
  background: #0264c4;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-search .search-input-wrapper {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
}

.mobile-search input {
  flex: 1;
  padding: 12px 16px;
  border: none;
  outline: none;
  font-size: 15px;
  background: transparent;
  color: white;
}

.mobile-search input::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.mobile-search button {
  background: transparent;
  border: none;
  padding: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-search button img {
  width: 20px;
  height: 20px;
  filter: brightness(0) invert(1);
}

@media (max-width: 768px) {
  .mobile-search {
    padding: 12px;
  }

  .mobile-search input {
    padding: 10px 14px;
    font-size: 14px;
  }

  .mobile-search button {
    padding: 10px;
  }
}

/* 移动端导航样式 */
.mobile-nav-wrapper {
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: calc(100vh - 80px);
  z-index: 99;
  overflow-y: auto;
}

.mobile-nav {
  position: fixed;
  top: 80px;
  left: 0;
  width: 100%;
  height: 50vh;
  background: #0264c4;
  z-index: 99;
  overflow-y: auto;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.mobile-menu {
  border: none;
  background: transparent;
}

:deep(.el-menu) {
  border-right: none;
  background: transparent;
}

:deep(.el-sub-menu__title) {
  height: 56px !important;
  line-height: 56px !important;
  font-size: 16px;
  padding: 0 24px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: white !important;
}

:deep(.el-sub-menu__title:hover),
:deep(.el-sub-menu__title:focus) {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

:deep(.el-menu-item) {
  height: 48px !important;
  line-height: 48px !important;
  font-size: 15px;
  padding: 0 48px !important;
  background-color: rgba(0, 0, 0, 0.15) !important;
  color: rgba(255, 255, 255, 0.9) !important;
}

:deep(.el-menu-item:hover),
:deep(.el-menu-item.is-active) {
  background-color: rgba(255, 255, 255, 0.1) !important;
  color: white !important;
}

:deep(.el-sub-menu__icon-arrow) {
  color: white !important;
}

@media (max-width: 768px) {
  .mobile-nav-overlay {
    top: 70px;
    height: calc(100vh - 70px);
  }

  .mobile-nav {
    top: 70px;
  }

  :deep(.el-sub-menu__title) {
    height: 50px !important;
    line-height: 50px !important;
    font-size: 15px;
  }

  :deep(.el-menu-item) {
    height: 44px !important;
    line-height: 44px !important;
    font-size: 14px;
  }
}

@media (max-width: 480px) {
  .mobile-nav-overlay {
    top: 60px;
    height: calc(100vh - 60px);
  }

  .mobile-nav {
    top: 60px;
  }
}
</style>
